import { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { SectionSurface } from '../../components/role-ui';
import { getReporteKilometros } from '../../lib/reportes';
import type { KilometroItem } from '../../lib/reportes';
import { avisar, SkeletonRows } from '../../components/ui-kit';

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '—';
  const parsed = new Date(dateString);
  if (Number.isNaN(parsed.getTime())) return String(dateString);
  return new Intl.DateTimeFormat('es-EC', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).format(parsed);
}

function formatTipoEntrega(tipo: string): string {
  const labels: Record<string, string> = {
    entrega: 'Entrega',
    envio: 'Envío',
    transferencia: 'Transferencia',
  };
  return labels[tipo] || tipo;
}

function formatKilometros(km: number): string {
  return `${km.toFixed(2)} km`;
}

export default function AdminKilometrosPanel() {
  const [items, setItems] = useState<KilometroItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filtros de fecha
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');

  // Paginación
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalKilometros, setTotalKilometros] = useState(0);

  const pageSize = 10;

  const loadData = async (pageNum: number) => {
    try {
      setLoading(true);
      setError('');
      const params: { fecha_desde?: string; fecha_hasta?: string; page: number; page_size: number } = {
        page: pageNum,
        page_size: pageSize,
      };
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;

      const data = await getReporteKilometros(params);
      setItems(data.items);
      setPage(data.page);
      setTotalPages(data.total_pages);
      setTotalItems(data.count);
      setTotalKilometros(data.total_kilometros);
    } catch (err: unknown) {
      setError((err as { message?: string })?.message ?? 'Error al cargar el reporte de kilómetros.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData(1);
  }, []);

  const handleBuscar = () => {
    setPage(1);
    void loadData(1);
  };

  const handleLimpiarFiltros = () => {
    setFechaDesde('');
    setFechaHasta('');
    setPage(1);
  };

  useEffect(() => {
    if (fechaDesde === '' && fechaHasta === '') {
      void loadData(1);
    }
  }, [fechaDesde, fechaHasta]);

  const handleExportarExcel = () => {
    if (items.length === 0) {
      avisar('No hay datos para exportar.', 'advertencia');
      return;
    }

    const data = items.map(item => ({
      'ID': item.id,
      'Fecha': formatDate(item.fecha),
      'Vehículo': item.vehiculo_placa,
      'Marca': item.vehiculo_marca || '—',
      'Modelo': item.vehiculo_modelo || '—',
      'Conductor': item.conductor_nombre,
      'Kilómetros recorridos': item.kilometros_recorridos,
      'Tipo de entrega': formatTipoEntrega(item.tipo_entrega),
    }));

    // Agregar totales al final
    data.push({
      'ID': '' as any,
      'Fecha': '',
      'Vehículo': '',
      'Marca': '',
      'Modelo': '',
      'Conductor': 'TOTAL',
      'Kilómetros recorridos': totalKilometros,
      'Tipo de entrega': '',
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Kilómetros');
    XLSX.writeFile(workbook, `Reporte_Kilometros_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleExportarPDF = () => {
    if (items.length === 0) {
      avisar('No hay datos para exportar.', 'advertencia');
      return;
    }

    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(16);
    doc.text('Reporte de Kilómetros Recorridos', 14, 20);
    doc.setFontSize(10);
    doc.text(`Generado: ${new Date().toLocaleDateString('es-EC')}`, 14, 28);
    if (fechaDesde || fechaHasta) {
      doc.text(`Filtro: ${fechaDesde || '—'} al ${fechaHasta || '—'}`, 14, 34);
    }

    const rows = items.map(item => [
      formatDate(item.fecha),
      item.vehiculo_placa,
      `${item.vehiculo_marca || ''} ${item.vehiculo_modelo || ''}`.trim() || '—',
      item.conductor_nombre,
      `${item.kilometros_recorridos.toFixed(2)} km`,
      formatTipoEntrega(item.tipo_entrega),
    ]);

    // Agregar fila de total
    rows.push(['', '', '', 'TOTAL', `${totalKilometros.toFixed(2)} km`, '']);

    autoTable(doc, {
      head: [['Fecha', 'Placa', 'Vehículo', 'Conductor', 'Kilómetros', 'Tipo']],
      body: rows,
      startY: fechaDesde || fechaHasta ? 40 : 34,
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [28, 25, 23], textColor: 255 },
      footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold' },
    });

    doc.save(`Reporte_Kilometros_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const inputCls =
    'w-full rounded-[14px] border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-700 outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-100';
  const labelCls = 'mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500';

  return (
    <section className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-end">
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-4 rounded-[var(--radius-card)] border border-stone-200 bg-stone-50 p-4">
        <div>
          <label className={labelCls}>Fecha desde</label>
          <input
            type="date"
            value={fechaDesde}
            onChange={e => setFechaDesde(e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Fecha hasta</label>
          <input
            type="date"
            value={fechaHasta}
            onChange={e => setFechaHasta(e.target.value)}
            className={inputCls}
          />
        </div>
        <button
          type="button"
          onClick={handleBuscar}
          disabled={loading}
          className="ui-btn ui-btn-primary"
        >
          Buscar
        </button>
        <button
          type="button"
          onClick={handleLimpiarFiltros}
          className="ui-btn ui-btn-secondary"
        >
          Limpiar filtros
        </button>
        <div className="flex gap-2 xl:ml-auto">
          <button
            type="button"
            onClick={handleExportarExcel}
            className="ui-btn ui-btn-success"
          >
            Exportar Excel
          </button>
          <button
            type="button"
            onClick={handleExportarPDF}
            className="ui-btn ui-btn-info"
          >
            Exportar PDF
          </button>
        </div>
      </div>

      {/* Resumen */}
      <div className="grid gap-4 xl:grid-cols-3">
        <article className="ui-card p-5">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Total registros</p>
          <p className="mt-4 text-3xl font-semibold text-stone-950">{totalItems}</p>
        </article>
        <article className="ui-card p-5">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Total kilómetros</p>
          <p className="mt-4 text-3xl font-semibold text-stone-950">{formatKilometros(totalKilometros)}</p>
        </article>
        <article className="ui-card p-5">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Página</p>
          <p className="mt-4 text-3xl font-semibold text-stone-950">{page} de {totalPages}</p>
        </article>
      </div>

      {error && (
        <div className="rounded-[var(--radius-panel)] border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700 shadow-sm">
          {error}
        </div>
      )}

      {/* Tabla */}
      <SectionSurface className="space-y-5">
        {loading ? (
          <SkeletonRows etiqueta="Cargando reporte..." />
        ) : items.length === 0 ? (
          <div className="rounded-[var(--radius-card)] border border-dashed border-stone-300 bg-white/70 p-10 text-center text-stone-500">
            No se encontraron viajes con kilómetros registrados para los filtros seleccionados.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="ui-tabla min-w-full border-separate border-spacing-y-2 text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-[0.18em] text-stone-500">
                    <th className="pb-2 pr-4 font-semibold">ID</th>
                    <th className="pb-2 pr-4 font-semibold">Fecha</th>
                    <th className="pb-2 pr-4 font-semibold">Placa</th>
                    <th className="pb-2 pr-4 font-semibold">Vehículo</th>
                    <th className="pb-2 pr-4 font-semibold">Conductor</th>
                    <th className="pb-2 pr-4 font-semibold text-right">Kilómetros</th>
                    <th className="pb-2 font-semibold">Tipo</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item.id} className="bg-stone-50/70 text-stone-700 align-top">
                      <td className="rounded-l-[16px] px-4 py-3 font-semibold text-stone-500">
                        #{item.id}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {formatDate(item.fecha)}
                      </td>
                      <td className="px-4 py-3 font-semibold">
                        {item.vehiculo_placa}
                      </td>
                      <td className="px-4 py-3 text-stone-500">
                        {[item.vehiculo_marca, item.vehiculo_modelo].filter(Boolean).join(' ') || '—'}
                      </td>
                      <td className="px-4 py-3">
                        {item.conductor_nombre}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-stone-900">
                        {formatKilometros(item.kilometros_recorridos)}
                      </td>
                      <td className="rounded-r-[16px] px-4 py-3">
                        <span className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wider ${
                          item.tipo_entrega === 'envio'
                            ? 'bg-sky-50 text-sky-700'
                            : item.tipo_entrega === 'transferencia'
                            ? 'bg-purple-50 text-purple-700'
                            : 'bg-emerald-50 text-emerald-700'
                        }`}>
                          {formatTipoEntrega(item.tipo_entrega)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            <div className="flex items-center justify-between border-t border-stone-200 bg-stone-50 px-4 py-3 rounded-b-[28px]">
              <span className="text-sm text-stone-500">Página {page} de {totalPages} ({totalItems} registros)</span>
              <div className="flex gap-2">
                <button
                  onClick={() => void loadData(page - 1)}
                  disabled={page <= 1 || loading}
                  className="rounded-xl border border-stone-200 bg-white px-3 py-1.5 text-sm font-semibold text-stone-700 transition hover:bg-stone-50 disabled:opacity-50"
                >
                  Anterior
                </button>
                <button
                  onClick={() => void loadData(page + 1)}
                  disabled={page >= totalPages || loading}
                  className="ui-btn ui-btn-secondary ui-btn-sm"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </>
        )}
      </SectionSurface>
    </section>
  );
}
